import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import appLogo from 'assets/app-logo-alt.png'
import PATHS from 'utils/paths'
import { getCurrentPathname, redirect } from 'utils/navigation'
import { logoutUser } from 'services/AuthService'
import { useDispatch, useSelector } from 'react-redux'
import { clearCurrentUserRoutine } from 'modules/auth/ducks/actions'
import history from 'utils/history'
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined'
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined'
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { selectCurrentUser } from 'modules/auth/ducks/selectors'
import { propOr } from 'ramda'
import NavigationContent from 'components/navigation/NavigationContent'

const GROUPS_EXPANDED_LS_KEY = 'sschool.navigation.groupsExpanded'

const GROUPS_MAP = {
  admin: 'Administracja',
  teacher: 'Nauczyciele i wypłaty',
  student: 'Uczniowie i rozliczenia',
  tools: 'Import i narzędzia'
}

const GROUP_ORDER_ADMIN = ['teacher', 'student', 'admin', 'tools']
const GROUP_ORDER_WORKER = ['teacher', 'student', 'admin']

const readExpandedMap = () => {
  try {
    const raw = localStorage.getItem(GROUPS_EXPANDED_LS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

const writeExpandedMap = map => {
  try {
    localStorage.setItem(GROUPS_EXPANDED_LS_KEY, JSON.stringify(map))
  } catch {
    // ignore
  }
}

const NavigationContentGrouped = () => {
  const currentUser = useSelector(selectCurrentUser)
  const userRole = propOr('', 'role', currentUser)

  const [pathname, setPathname] = useState(getCurrentPathname())
  const [expandedMap, setExpandedMap] = useState(readExpandedMap)

  const dispatch = useDispatch()

  const adminNavLinks = [
    { label: 'Pracownicy', path: PATHS.workers, icon: <AssignmentIndOutlinedIcon />, group: 'admin' },
    { label: 'Nauczyciele', path: PATHS.teachers, icon: <GroupOutlinedIcon />, group: 'teacher' },
    { label: 'Uczniowie', path: PATHS.students, icon: <SchoolOutlinedIcon />, group: 'student' },
    { label: 'Fiolety', path: PATHS.newStudents, icon: <GroupAddIcon />, group: 'student' },
    { label: 'Dłużnicy', path: PATHS.debtors, icon: <CurrencyExchangeOutlinedIcon />, group: 'student' },
    { label: 'Problemy z wpłatami', path: PATHS.paymentProblems, icon: <WarningAmberOutlinedIcon />, group: 'student' },
    { label: 'Uczniowie bez wpłat', path: PATHS.studentsNoPayments, icon: <SchoolOutlinedIcon />, group: 'student' },
    { label: 'Uczniowie do usunięcia', path: PATHS.studentsToDelete, icon: <SchoolOutlinedIcon />, group: 'student' },
    { label: 'Wypłaty nauczycieli', path: PATHS.teachersIncomes, icon: <PointOfSaleOutlinedIcon />, group: 'teacher' },
    { label: 'Aktualność panelu', path: PATHS.panelTopicality, icon: <SentimentSatisfiedOutlinedIcon />, group: 'admin' },
    { label: 'Statystyki nauczycieli', path: PATHS.teacherStats, icon: <QueryStatsIcon />, group: 'teacher' },
    { label: 'Archiwum nauczycieli', path: PATHS.archivedTeachers, icon: <Inventory2OutlinedIcon />, group: 'teacher' },
    { label: 'Import lekcji', path: PATHS.calendarSync, icon: <CalendarMonthIcon />, group: 'tools' },
    { label: 'Finanse', path: PATHS.finances, icon: <SavingsOutlinedIcon />, group: 'admin' },
    { label: 'Narzędzia', path: PATHS.tools, icon: <BuildOutlinedIcon />, group: 'tools' },
    { label: 'Ustawienia', path: PATHS.settings, icon: <SettingsOutlinedIcon />, group: 'admin' },
    { label: 'Profil użytkownika', path: PATHS.profile, icon: <PermIdentityOutlinedIcon />, group: 'admin' }
  ]

  const workerNavLinks = [
    { label: 'Pracownicy', path: PATHS.workers, icon: <AssignmentIndOutlinedIcon />, group: 'admin' },
    { label: 'Nauczyciele', path: PATHS.teachers, icon: <GroupOutlinedIcon />, group: 'teacher' },
    { label: 'Uczniowie', path: PATHS.students, icon: <SchoolOutlinedIcon />, group: 'student' },
    { label: 'Fiolety', path: PATHS.newStudents, icon: <GroupAddIcon />, group: 'student' },
    { label: 'Dłużnicy', path: PATHS.debtors, icon: <CurrencyExchangeOutlinedIcon />, group: 'student' },
    { label: 'Problemy z wpłatami', path: PATHS.paymentProblems, icon: <WarningAmberOutlinedIcon />, group: 'student' },
    { label: 'Uczniowie bez wpłat', path: PATHS.studentsNoPayments, icon: <SchoolOutlinedIcon />, group: 'student' },
    { label: 'Uczniowie do usunięcia', path: PATHS.studentsToDelete, icon: <SchoolOutlinedIcon />, group: 'student' },
    { label: 'Wypłaty nauczycieli', path: PATHS.teachersIncomes, icon: <PointOfSaleOutlinedIcon />, group: 'teacher' },
    { label: 'Aktualność panelu', path: PATHS.panelTopicality, icon: <SentimentSatisfiedOutlinedIcon />, group: 'admin' },
    { label: 'Statystyki nauczycieli', path: PATHS.teacherStats, icon: <QueryStatsIcon />, group: 'teacher' },
    { label: 'Archiwum nauczycieli', path: PATHS.archivedTeachers, icon: <Inventory2OutlinedIcon />, group: 'teacher' },
    { label: 'Ustawienia', path: PATHS.settings, icon: <SettingsOutlinedIcon />, group: 'admin' },
    { label: 'Profil użytkownika', path: PATHS.profile, icon: <PermIdentityOutlinedIcon />, group: 'admin' }
  ]

  const isGroupedRole = userRole === 'admin' || userRole === 'worker'
  const groupOrder =
    userRole === 'admin'
      ? GROUP_ORDER_ADMIN
      : userRole === 'worker'
        ? GROUP_ORDER_WORKER
        : []
  const flatLinks =
    userRole === 'admin' ? adminNavLinks : userRole === 'worker' ? workerNavLinks : []

  const sections = useMemo(
    () =>
      groupOrder
        .map(key => ({
          key,
          title: GROUPS_MAP[key],
          items: flatLinks.filter(l => l.group === key)
        }))
        .filter(s => s.items.length > 0),
    [flatLinks, groupOrder]
  )

  const activeGroupKey = useMemo(() => {
    const link = flatLinks.find(l => pathname.includes(l.path))
    return link ? link.group : null
  }, [flatLinks, pathname])

  useEffect(() => {
    if (!isGroupedRole || !activeGroupKey) return
    setExpandedMap(prev => {
      if (prev[activeGroupKey] === true) return prev
      const next = { ...prev, [activeGroupKey]: true }
      writeExpandedMap(next)
      return next
    })
  }, [activeGroupKey, pathname, isGroupedRole])

  useEffect(() => {
    const unlisten = history.listen(({ pathname: p }) => setPathname(p))
    return () => unlisten()
  }, [])

  const handleClearUser = () => {
    dispatch(clearCurrentUserRoutine())
  }

  const handleLogout = () => {
    logoutUser(handleClearUser)
    setPathname(getCurrentPathname())
  }

  const handleLinkClick = path => () => {
    redirect(path)
    setPathname(path)
  }

  const isGroupOpen = key => {
    if (Object.prototype.hasOwnProperty.call(expandedMap, key)) {
      return expandedMap[key]
    }
    return true
  }

  const handleToggleGroup = key => () => {
    setExpandedMap(prev => {
      const open = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : true
      const next = { ...prev, [key]: !open }
      writeExpandedMap(next)
      return next
    })
  }

  if (!isGroupedRole) {
    return <NavigationContent />
  }

  return (
    <Wrapper>
      <div>
        <LogoWrapper>
          <img src={appLogo} alt='Smart School' />
        </LogoWrapper>
        <MenuWrapper>
          {sections.map(section => {
            const open = isGroupOpen(section.key)
            return (
              <GroupBlock key={section.key}>
                <GroupHeader onClick={handleToggleGroup(section.key)} type='button'>
                  <GroupTitle>{section.title}</GroupTitle>
                  <Chevron $open={open}>
                    <ExpandMoreIcon />
                  </Chevron>
                </GroupHeader>
                <Collapse in={open}>
                  <GroupBody>
                    {section.items.map(link => (
                      <Link
                        active={pathname.includes(link.path)}
                        onClick={handleLinkClick(link.path)}
                        key={link.path}
                      >
                        {link.icon}
                        {link.label}
                      </Link>
                    ))}
                  </GroupBody>
                </Collapse>
              </GroupBlock>
            )
          })}
        </MenuWrapper>
      </div>
      <div>
        <Link onClick={handleLogout}>
          <PowerSettingsNewOutlinedIcon />
          Wyloguj
        </Link>
      </div>
    </Wrapper>
  )
}

export default NavigationContentGrouped

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const LogoWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  img {
    max-height: 60px;
  }
`

const Link = styled.div`
  color: ${({ active, theme }) => (active ? theme.colors.primary.main : '#fff')};
  padding: 2px 10px;
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 3px;
  transition: all 0.3s;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: ${({ active, theme }) =>
    active ? theme.colors.secondary.main : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary.main};
    color: ${({ theme }) => theme.colors.primary.main};
  }
`

const MenuWrapper = styled.div`
  margin-top: 20px;
`

const GroupBlock = styled.div`
  margin-bottom: 6px;
`

const GroupHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  margin: 0;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`

const GroupTitle = styled.span`
  flex: 1;
`

const Chevron = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`

const GroupBody = styled.div`
  padding: 4px 0 4px 4px;
`
